/**
 * Guards mapbox-gl-draw against clicks on "orphaned" map targets.
 *
 * Draw paints a selected polygon's vertices into a GeoJSON source, but only
 * refreshes that source on the next animation frame (`Store.render` is
 * requestAnimationFrame-batched) after which mapbox-gl still has to re-parse
 * the tile. So for a short window after features are removed from the draw
 * store, the map keeps hit-testing vertices whose parent feature is already
 * gone. The zone editor hits that window every time it re-syncs the map
 * (saving a zone, switching day), because it wipes every feature and re-adds
 * it under a fresh id.
 *
 * A click landing on such an orphan makes `simple_select` hand a dangling id
 * to `direct_select`, which throws "You must provide a featureId to enter
 * direct_select mode". Worse, the throw happens halfway through
 * `events.changeMode`, so Draw is left with a mode *name* that no longer
 * matches its live mode *handler* and every later map click misbehaves until
 * the page is reloaded. See Sentry PRODUCE-1Y.
 */

/** `properties.meta` values Draw puts on the things it renders. */
export const DRAW_META = {
  FEATURE: "feature",
  VERTEX: "vertex",
  MIDPOINT: "midpoint",
} as const;

/** The only thing a guard needs from the draw store: does this feature exist? */
export interface DrawFeatureLookup {
  getFeature(featureId: string): unknown;
}

/** The only thing a guard needs from a Draw pointer event. */
export interface DrawTargetEvent {
  featureTarget?: {
    properties?: {
      meta?: string;
      id?: string;
      parent?: string;
      // Draw puts more on its targets (coord_path, active, ...).
      [key: string]: unknown;
    };
  };
}

/** The slice of the public Draw API used to change modes safely. */
export interface DrawModeChanger {
  get(featureId: string): unknown;
  getMode(): string;
  changeMode(mode: string, options?: Record<string, unknown>): unknown;
}

/** Signature of a Draw mode's pointer handlers. */
export type DrawModeHandler = (state: unknown, event: DrawTargetEvent) => unknown;

/** A Draw mode object: a bag of `onX` handlers plus mode-specific helpers. */
export type DrawMode = Record<string, unknown> & {
  onClick?: DrawModeHandler;
  onTap?: DrawModeHandler;
};

/**
 * True when the click targets a Draw shape whose feature is no longer in the
 * store. Clicks on the bare map are not our business — those are `false`, so
 * the mode handles them as usual.
 */
export function isOrphanedDrawTarget(
  lookup: DrawFeatureLookup,
  event: DrawTargetEvent | null | undefined,
): boolean {
  const properties = event?.featureTarget?.properties;
  if (!properties) return false;

  switch (properties.meta) {
    case DRAW_META.FEATURE:
      // A Draw target that names no feature is dangling by definition.
      return !properties.id || !lookup.getFeature(properties.id);
    case DRAW_META.VERTEX:
    case DRAW_META.MIDPOINT:
      return !properties.parent || !lookup.getFeature(properties.parent);
    default:
      return false;
  }
}

/**
 * Wraps a select mode so clicks on orphaned targets are dropped instead of
 * crashing. Returns a new mode object; the base mode is left untouched.
 *
 * Dropped clicks deliberately return `undefined` so Draw still runs its
 * render pass, which is what flushes the stale vertices off the map.
 */
export function createSafeSelectMode<T extends DrawMode>(base: T): T {
  const guard = (handler: DrawModeHandler): DrawModeHandler =>
    function (this: DrawFeatureLookup, state: unknown, event: DrawTargetEvent) {
      if (isOrphanedDrawTarget(this, event)) return undefined;
      return handler.call(this, state, event);
    };

  // Draw registers a hit-test selector per handler *key present* on the mode
  // (object_to_mode.js), so adding a key the base mode does not define would
  // register a live no-op listener.
  const safe: DrawMode = { ...base };
  if (base.onClick) safe.onClick = guard(base.onClick);
  if (base.onTap) safe.onTap = guard(base.onTap);
  return safe as T;
}

/**
 * Builds the `modes` map for the MapboxDraw constructor with `simple_select`
 * replaced by its guarded twin. Every other mode passes through unchanged.
 *
 * Only `simple_select` needs guarding: it is the one built-in whose click
 * paths hand a feature id to another mode (`clickOnVertex` → direct_select,
 * `clickOnFeature` → `getFeature(id).type`).
 */
export function withSafeSimpleSelect(modes: Record<string, unknown>): Record<string, unknown> {
  const simpleSelect = modes.simple_select as DrawMode | undefined;
  // Never let a missing built-in take the whole map down with it.
  if (!simpleSelect) return modes;
  return { ...modes, simple_select: createSafeSelectMode(simpleSelect) };
}

/**
 * Enters vertex-edit mode for a feature, but only if that feature is really
 * still on the map. Our own "Edit zone" buttons hold feature ids that go
 * stale whenever the map re-syncs; handing such an id to Draw throws.
 *
 * @returns whether the mode change actually happened.
 */
export function enterDirectSelect(
  draw: DrawModeChanger | null | undefined,
  featureId: string | null | undefined,
): boolean {
  if (!draw || !featureId) return false;
  if (!draw.get(featureId)) return false;
  draw.changeMode("direct_select", { featureId });
  return true;
}

/**
 * Drops any selection and returns Draw to plain select mode.
 *
 * Note this also *ends* an in-progress drawing: Draw runs `onStop` on the way
 * out, and `draw_polygon.onStop` fires `draw.create` for a polygon that
 * already has three points. Only call it where finishing is what you mean —
 * use `clearSelectionUnlessDrawing` otherwise.
 */
export function resetDrawSelection(draw: DrawModeChanger | null | undefined): void {
  if (!draw) return;
  draw.changeMode("simple_select", { featureIds: [] });
}

/**
 * Clears the selection ahead of removing features — but leaves a drawing in
 * progress alone, so a background refresh can never save a polygon the user
 * was still placing points on. Draw names its creating modes `draw_*`
 * (draw_polygon, draw_line_string, draw_point).
 */
export function clearSelectionUnlessDrawing(draw: DrawModeChanger | null | undefined): void {
  if (!draw || draw.getMode().startsWith("draw_")) return;
  resetDrawSelection(draw);
}
